import { useEffect } from "react";

const INACTIVITY_TIMEOUT = 150 * 60 * 1000; // 15 minutes in milliseconds

/**
 * Custom hook that monitors user inactivity and triggers a callback after the specified timeout period.
 * Resets the timer whenever there's user activity (mouse move, key press, click, or scroll).
 * @param onInactivityTimeout - Callback function to execute when inactivity timeout occurs
 */
export function useInactivityTimeout(onInactivityTimeout: () => void) {
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    const resetTimeout = () => {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout
      timeoutId = setTimeout(() => {
        onInactivityTimeout();
      }, INACTIVITY_TIMEOUT);
    };

    const eventsToMonitor = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];

    const resetTimeoutOnActivity = () => {
      resetTimeout();
    };

    resetTimeout();

    for (const event of eventsToMonitor) {
      document.addEventListener(event, resetTimeoutOnActivity);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      for (const event of eventsToMonitor) {
        document.removeEventListener(event, resetTimeoutOnActivity);
      }
    };
  }, [onInactivityTimeout]);
}
